import React, { FC } from 'react';
import * as tokens from '@utmost.co/utmost-design-tokens';
import DialogActions, { DialogActionsProps } from '@mui/material/DialogActions';
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import MuiDialog, { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import { useBpDesignTheme } from '../../setup';
import { BpDesignSystem } from '../../types';
import { Button, ButtonProps } from '../Button/Button';
import { Fade } from '../Fade';
import { LinearProgress } from '../Progress';
import { Typography, TypographyProps } from '../Typography/Typography';
import { styled } from '../../styles';
import { IconButton, IconButtonProps } from '../IconButton/IconButton';
import type { DialogBreakpoint, DialogHeight, DialogWidth } from './size.types';
import { dialogSizePropsToMuiProps } from './dialogSizePropsToMuiProps';

const isTestEnv = process.env.NODE_ENV === 'test';

export type DialogProps = BpDesignSystem.PropsWithChildren<
  Omit<MuiDialogProps, 'slotProps' | 'title' | 'maxWidth' | 'fullWidth'> & {
    title?: React.ReactNode;
    subTitle?: React.ReactNode;
    onClose: () => void;
    onSubmit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    actions?: React.ReactNode;
    loading?: boolean;
    slotProps?: MuiDialogProps['slotProps'] & {
      title?: TypographyProps;
      subTitle?: TypographyProps;
      actions?: DialogActionsProps;
      content?: DialogContentProps;
      submitButton?: ButtonProps;
      cancelButton?: ButtonProps;
      closeButton?: Partial<Omit<IconButtonProps, 'ref'>>;
    };
    minWidth?: DialogBreakpoint;
    width?: DialogWidth;
    maxWidth?: DialogWidth;
    height?: DialogHeight;
    maxHeight?: DialogHeight;
    show?: {
      /**
       * @default: true
       * @description: This button must be explicitly set to false to hide it.
       */
      closeButton?: boolean;
      cancelButton?: boolean;
      submitButton?: boolean;
    };
    disableBackdropClick?: boolean;
    disableEscapeKeyDown?: boolean;
  }
>;

const DEFAULT_SHOW_PROP: DialogProps['show'] = {
  closeButton: true
};

export const Dialog: FC<DialogProps> = ({
  dataTest = 'mui-dialog',
  onClose,
  onSubmit,
  actions,
  title,
  children,
  subTitle,
  scroll = 'paper',
  slotProps,
  show = DEFAULT_SHOW_PROP,
  disableBackdropClick = true,
  disableEscapeKeyDown,
  loading,
  minWidth = 'sm',
  width: dialogWidth,
  maxWidth: dialogMaxWidth,
  height: dialogHeight,
  maxHeight: dialogMaxHeight,
  sx: dialogSx,
  ...dialogProps
}) => {
  const {
    title: titleProps,
    content: contentProps,
    subTitle: subTitleProps,
    actions: actionsProps,
    cancelButton: cancelButtonProps,
    closeButton: closeButtonProps,
    submitButton: submitButtonProps,
    ...dialogSlotProps
  } = slotProps ?? {};

  const { breakpoints } = useBpDesignTheme();

  const { fullWidth, maxWidth, sx } = dialogSizePropsToMuiProps(
    { breakpoint: minWidth, value: breakpoints.values[minWidth] },
    dialogWidth,
    dialogMaxWidth,
    dialogHeight,
    dialogMaxHeight
  );

  const targetShow = { ...DEFAULT_SHOW_PROP, ...show };
  return (
    <MuiDialog
      data-test={dataTest}
      slotProps={dialogSlotProps}
      disableEnforceFocus={isTestEnv ? true : undefined}
      {...dialogProps}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      sx={{ ...dialogSx, ...sx } as DialogProps['sx']}
      onClose={(e, reason) => {
        if (
          (disableBackdropClick && reason === 'backdropClick') ||
          (disableEscapeKeyDown && reason === 'escapeKeyDown')
        ) {
          return;
        }
        onClose?.(e, reason);
      }}
    >
      <DialogTitle>
        <StyledProgressWrapper>
          <Fade in={loading} unmountOnExit>
            <LinearProgress />
          </Fade>
        </StyledProgressWrapper>
        {!!title && (
          <Typography dataTest="modal__title" variant="heading" small component="div" {...titleProps}>
            {title}
          </Typography>
        )}
        {!!subTitle && (
          <StyledSubTitle data-test="modal__subtitle">
            <Typography color={tokens.colorOnSurfaceVariant} {...subTitleProps}>
              {subTitle}
            </Typography>
          </StyledSubTitle>
        )}
      </DialogTitle>
      {!!targetShow?.closeButton && (
        <StyledIconButton
          iconName="close"
          aria-label="close"
          dataTest="dialog_close__button"
          {...closeButtonProps}
          onClick={onClose}
        />
      )}
      {!!children && (
        <StyledContent {...contentProps} data-test="modal__content">
          <div data-test="content_container">
            <StyledContentTypography>{children}</StyledContentTypography>
          </div>
        </StyledContent>
      )}
      {(!!actions || targetShow?.cancelButton || (targetShow?.submitButton && onSubmit)) && (
        <DialogActions {...actionsProps}>
          {!!targetShow?.cancelButton && (
            <Button
              color="primary"
              children="Cancel"
              dataTest="dialog-cancel-button"
              dataTestType="primary-text"
              {...cancelButtonProps}
              onClick={onClose}
            />
          )}
          {actions}
          {!!targetShow?.submitButton && onSubmit && (
            <Button color="primary" variant="contained" children="Save" {...submitButtonProps} onClick={onSubmit} />
          )}
        </DialogActions>
      )}
    </MuiDialog>
  );
};

const StyledSubTitle = styled('div')`
  .MuiTypography-root + & {
    padding-top: ${({ theme }) => theme.spacingSize.m};
  }  
`;

const StyledContent = styled(DialogContent)`
    padding-top: 0;
`;
const StyledContentTypography = styled(Typography)`
    height: 100%;
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    top: 12px;
    right: 12px;
`;

const StyledProgressWrapper = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
`;
